import React from 'react';
import { Plus, List, Upload, User, Phone, Mail, UserPlus, Calendar, MapPin, MessageSquare, Activity, Send } from 'lucide-react';

interface FormProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  formData: any;
  setFormData: (data: any) => void;
  loading: boolean;
  handleFormSubmit: () => void;
  handleFileUpload: () => void;
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  uploadResult: any;
  employees: any[];
  customerSupportUsers: any[];
  renderEditableCell: (value: string, field: string, userId: number) => any;
  renderStatusCapsule: (status: string) => any;
  handleEdit: (user: any) => void;
  handleSave: (userId: number) => void;
  handleCancel: () => void;
  handleSendClick: (mobileNo: string, userId: number) => void;
  editingUser: number | null;
  fetchEmployees: () => void;
  handleDownloadTemplate: () => void;
  handleEditInstruction: (userId: number, currentInstruction: string) => void;
  handleSaveInstruction: (userId: number) => void;
  handleCancelInstruction: () => void;
  editingInstruction: number | null;
  instructionData: string;
  setInstructionData: (data: string) => void;
}

const UnregisteredUsersForm: React.FC<FormProps> = ({
  activeTab,
  setActiveTab,
  formData,
  setFormData,
  loading,
  handleFormSubmit,
  handleFileUpload,
  selectedFile,
  setSelectedFile,
  uploadResult,
  employees,
  customerSupportUsers,
  renderEditableCell,
  renderStatusCapsule,
  handleEdit,
  handleSave,
  handleCancel,
  handleSendClick,
  editingUser,
  fetchEmployees,
  handleDownloadTemplate,
  handleEditInstruction,
  handleSaveInstruction,
  handleCancelInstruction,
  editingInstruction,
  instructionData,
  setInstructionData
}) => {
  return (
    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl overflow-hidden">
      <div className="flex border-b border-white/20">
        <button
          onClick={() => setActiveTab('form')}
          className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
            activeTab === 'form'
              ? 'text-yellow-300 border-b-2 border-yellow-300 bg-white/5'
              : 'text-gray-300 hover:text-yellow-200'
          }`}
        >
          <Plus className="w-4 h-4 inline mr-2" />
          Add User
        </button>
        <button
          onClick={() => setActiveTab('upload')}
          className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
            activeTab === 'upload'
              ? 'text-yellow-300 border-b-2 border-yellow-300 bg-white/5'
              : 'text-gray-300 hover:text-yellow-200'
          }`}
        >
          <Upload className="w-4 h-4 inline mr-2" />
          Upload Excel
        </button>
        <button
          onClick={() => setActiveTab('list')}
          className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
            activeTab === 'list'
              ? 'text-yellow-300 border-b-2 border-yellow-300 bg-white/5'
              : 'text-gray-300 hover:text-yellow-200'
          }`}
        >
          <List className="w-4 h-4 inline mr-2" />
          Users List ({employees.length})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'form' && (
        <div className="p-8">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-yellow-300 mb-3">
                  <User className="w-4 h-4" />
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 text-white placeholder-gray-400"
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-yellow-300 mb-3">
                  <Phone className="w-4 h-4" />
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  value={formData.mobile_no}
                  onChange={(e) => setFormData({...formData, mobile_no: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 text-white placeholder-gray-400"
                  placeholder="Enter mobile number"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-yellow-300 mb-3">
                  <UserPlus className="w-4 h-4" />
                  Instruction
                </label>
                <textarea
                  value={formData.instruction}
                  onChange={(e) => setFormData({...formData, instruction: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 text-white placeholder-gray-400 h-24 resize-none"
                  placeholder="Enter instruction"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-yellow-300 mb-3">
                  <UserPlus className="w-4 h-4" />
                  Assigned To
                </label>
                <select
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 text-white placeholder-gray-400"
                >
                  <option value="" className="bg-gray-800">Select user</option>
                  {customerSupportUsers.map((user) => (
                    <option key={user.admin_id} value={user.username} className="bg-gray-800">
                      {user.username}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-yellow-300 mb-3">
                  <MessageSquare className="w-4 h-4" />
                  Feedback
                </label>
                <textarea
                  value={formData.feedback}
                  onChange={(e) => setFormData({...formData, feedback: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 text-white placeholder-gray-400 h-24 resize-none"
                  placeholder="Enter feedback"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-yellow-300 mb-3">
                  <Activity className="w-4 h-4" />
                  Status
                </label>
                <input
                  type="text"
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 text-white placeholder-gray-400"
                  placeholder="Enter status"
                />
              </div>
            </div> */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-yellow-300 mb-3">
                  <Activity className="w-4 h-4" />
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 text-white placeholder-gray-400"
                >
                  <option value="" className="bg-gray-800">Select priority</option>
                  <option value="High" className="bg-gray-800">High</option>
                  <option value="Medium" className="bg-gray-800">Medium</option>
                  <option value="Low" className="bg-gray-800">Low</option>
                </select>
              </div>
            </div>

            <button 
              onClick={handleFormSubmit}
              disabled={loading}
              className="w-full bg-yellow-300 text-black py-3 px-4 rounded-xl font-medium hover:bg-yellow-400 transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus className="w-4 h-4" />
              {loading ? 'Adding...' : 'Add User'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'upload' && (
        <div className="p-8">
          <div className="max-w-lg mx-auto space-y-6">
            <div className="text-center">
              <Upload className="w-16 h-16 mx-auto text-yellow-300 mb-4" />
              <h3 className="text-lg font-semibold text-yellow-300 mb-2">Upload Excel File</h3>
              <p className="text-sm text-gray-300 mb-6">Upload an Excel file with unregistered user data</p>
            </div>
            
            <div className="border-2 border-dashed border-white/30 rounded-xl p-6 text-center hover:border-yellow-300 transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="hidden"
                id="excel-upload"
              />
              <label
                htmlFor="excel-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="w-8 h-8 text-yellow-300 mb-2" />
                <span className="text-sm font-medium text-white">
                  Click to upload Excel file
                </span>
                <span className="text-xs text-gray-400 mt-1">Supports .xlsx and .xls files</span>
              </label>
            </div>

            <button
              onClick={handleFileUpload}
              disabled={!selectedFile}
              className="w-full px-4 py-2 bg-yellow-300 text-black rounded-xl font-semibold hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md"
            >
              Upload
            </button>

            <button
              onClick={handleDownloadTemplate}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all duration-200 shadow-md flex items-center justify-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Download Template
            </button>

            <div className="bg-blue-500/10 rounded-lg border border-blue-500/20 p-4">
              <h4 className="font-medium text-blue-300 mb-2">Excel Format Requirements:</h4>
              <ul className="text-sm text-blue-200 space-y-1">
                <li>• Name (required)</li>
                <li>• Mobile No. (required)</li>
                <li>• Instruction</li>
                <li>• Status</li>
                <li>• Feedback</li>
                <li>• Assigned To</li>
                <li>• Priority (High/Medium/Low)</li>
              </ul>
            </div>

            {uploadResult && (
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <p className="text-green-300">✓ {uploadResult.message}</p>
                <p className="text-sm text-gray-400">Success: {uploadResult.success_count}, Errors: {uploadResult.error_count}</p>
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-red-300 text-sm">Errors:</p>
                    <ul className="text-xs text-red-200 mt-1">
                      {uploadResult.errors.slice(0, 5).map((error: string, index: number) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'list' && (
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold text-yellow-300">Unregistered Users List</h3>
              <p className="text-sm text-gray-300">Manage and view all unregistered users ({employees.length})</p>
            </div>
            <button
              onClick={fetchEmployees}
              className="px-4 py-2 bg-blue-500/30 text-white rounded-md hover:bg-blue-500/50 border border-blue-400/50 backdrop-blur-sm transition-all duration-200 font-medium"
            >
              Refresh
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1200px]">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left p-3 text-yellow-300">Name</th>
                  <th className="text-left p-3 text-yellow-300">Mobile</th>
                  <th className="text-left p-3 text-yellow-300">Instruction</th>
                  <th className="text-left p-3 text-yellow-300">Status</th>
                  <th className="text-left p-3 text-yellow-300">Feedback</th>
                  <th className="text-left p-3 text-yellow-300">Assigned To</th>
                  <th className="text-left p-3 text-yellow-300">Created</th>
                  <th className="text-left p-3 text-yellow-300 w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((user) => (
                  <tr key={user.id} className="border-b border-white/10 hover:bg-white/5">
                    <td className="p-3 text-white">
                      <div className="flex items-center gap-2">
                        <span>{user.name}</span>
                        {user.tag && (
                          <span className={`px-2 py-1 text-xs rounded-full border ${
                            user.tag === 'matched user' 
                              ? 'bg-green-300/20 text-green-300 border-green-300/30'
                              : 'bg-yellow-300/20 text-yellow-300 border-yellow-300/30'
                          }`}>
                            {user.tag}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-white">{renderEditableCell(user.mobile_no || '-', 'mobile_no', user.id)}</td>
                    <td className="p-3 text-white">
                      {editingInstruction === user.id ? (
                        <input
                          type="text"
                          value={instructionData}
                          onChange={(e) => setInstructionData(e.target.value)}
                          className="bg-white/10 text-white text-xs p-1 rounded border border-white/20 w-full"
                        />
                      ) : (
                        renderEditableCell(user.instruction, 'instruction', user.id)
                      )}
                    </td>
                    <td className="p-3 text-white">
                      {editingUser === user.id ? (
                        renderEditableCell(user.status, 'status', user.id)
                      ) : (
                        renderStatusCapsule(user.status || '-')
                      )}
                    </td>
                    <td className="p-3 text-white">{renderEditableCell(user.feedback, 'feedback', user.id)}</td>
                    <td className="p-3 text-white">{renderEditableCell(user.assigned_to || '-', 'assigned_to', user.id)}</td>
                    <td className="p-3 text-white">{new Date(user.created_at).toLocaleDateString()}</td>
                    <td className="p-3 w-40">
                      <div className="w-full">
                        {editingUser === user.id ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleSave(user.id)}
                              className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 border border-green-500 font-semibold shadow-md w-full"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancel}
                              className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 border border-red-500 font-semibold shadow-md w-full"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEdit(user)}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 border border-blue-500 font-semibold shadow-md w-full"
                            >
                              Edit
                            </button>
                            {editingInstruction === user.id ? (
                              <>
                                <button
                                  onClick={() => handleSaveInstruction(user.id)}
                                  className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 border border-green-500 font-semibold shadow-md w-full"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelInstruction}
                                  className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 border border-red-500 font-semibold shadow-md w-full"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => handleEditInstruction(user.id, user.instruction)}
                                className="px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 border border-purple-500 font-semibold shadow-md w-full"
                              >
                                Edit Inst
                              </button>
                            )}
                            <button
                              onClick={() => handleSendClick(user.mobile_no || '', user.id)}
                              className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 border border-green-500 flex items-center justify-center gap-1 font-semibold shadow-md w-full"
                            >
                              <Send size={10} />
                              Send
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {employees.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                No unregistered users found. Upload an Excel file to get started.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnregisteredUsersForm;